import { types, flow, getRoot } from 'mobx-state-tree'

import * as StringTools from '~/utilities/string'
import USER_VALIDATIONS, { USER_ASYNC_VALIDATIONS } from '~/utilities/userValidations'

import * as UserService from '~/services/user'
import * as AuthService from '~/services/auth'

import UserPaymentProcessor from '~/stores/models/UserPaymentProcessor'
import UserCommunicationsPreferences from '~/stores/models/UserCommunicationsPreferences'
import buildPersistedChangesStore from '~/stores/models/persistedChanges'
import buildCustomValidationStore from '~/stores/models/customValidation'

const WINIT_LAW_USER_TYPE = 'winitLaw'

const UserModelBase = types
  .model('User', {
    // hold temporary user password (for updating)
    password: types.optional(types.string, ''),
    passwordConfirmation: types.optional(types.string, ''),

    // If the account was created with a random password to let user know they can pick a password.
    accountCreatedWithRandomPassword: types.optional(types.boolean, false),
    // Actual user fields
    objectId: types.maybeNull(types.string),
    authData: types.frozen({}),
    blockUser: types.optional(types.boolean, false),
    accountDeleted: types.optional(types.boolean, false),
    sessionToken: types.maybeNull(types.string),
    name: types.optional(types.string, ''),
    username: types.optional(types.string, ''),
    credits: types.optional(types.number, 0),
    gender: types.optional(types.string, ''),
    emailVerified: types.maybeNull(types.boolean),
    email: types.optional(types.string, ''),
    phoneNumber: types.optional(types.string, ''),
    phoneNumberType: types.maybeNull(types.string),
    phoneNumberVerified: types.maybeNull(types.boolean, false),
    createdAt: types.maybeNull(types.Date),
    updatedAt: types.maybeNull(types.Date),
    paymentProcessor: types.optional(UserPaymentProcessor, {}),
    communicationsPreferences: types.optional(UserCommunicationsPreferences, {}),
    hasPaymentMethod: types.maybeNull(types.boolean),
    last4ssn: types.optional(types.string, ''),
    LastFour: types.optional(types.string, ''),
    licenseState: types.optional(types.string, ''),
    licenseID: types.optional(types.string, ''),
    // UTM query parameters for users coming from ads
    utm: types.frozen({}),
    // Google Click ID for users coming from Google Ads
    gclid: types.optional(types.string, ''),
    // Microsoft Click ID for users coming from Microsoft Advertising
    msclkid: types.optional(types.string, ''),
    // Address fields
    address: types.maybe(types.optional(types.string, '')),
    city: types.maybe(types.optional(types.string, '')),
    countryState: types.maybe(types.optional(types.string, '')),
    zipcode: types.maybe(types.optional(types.string, '')),
    userType: types.maybe(types.optional(types.string, '')),
    origin: types.maybe(types.optional(types.string, '')),
    isLegacyNJ: types.optional(types.boolean, false),
    source: types.optional(types.string, 'dashboard'),
  })
  .preProcessSnapshot((snapshot) => {
    if (!snapshot) return null
    // this function will run on a snapshot to format it before submitting it to the store.
    const parsedSnapshot = {}
    const keys = Object.keys(snapshot)
    keys.forEach((key) => {
      const input = snapshot[key]
      switch (key) {
        case 'createdAt':
        case 'updatedAt':
          parsedSnapshot[key] = input ? new Date(input) : null
          break
        default:
          parsedSnapshot[key] = input
          break
      }
    })

    return parsedSnapshot
  })
  .views((self) => ({
    get isMissingInformation() {
      return (
        self.objectId &&
        (!self.lastValuesSaved.name ||
          !self.lastValuesSaved.email ||
          !self.lastValuesSaved.username ||
          !self.lastValuesSaved.phoneNumber)
      )
    },
    get firstInitial() {
      const { firstName } = self
      if (firstName) {
        return firstName[0]
      }
      return ''
    },
    get firstName() {
      return StringTools.firstName(self.name)
    },
    get lastInitial() {
      const { lastName } = self
      if (lastName) {
        return lastName[0]
      }
      return ''
    },
    get lastName() {
      return StringTools.lastName(self.name)
    },
    get hasFullAddress() {
      return (
        self.address &&
        self.address.length &&
        self.city &&
        self.city.length &&
        self.countryState &&
        self.countryState.length &&
        self.zipcode &&
        self.zipcode.length
      )
    },
    get usesThirdPartyCredentials() {
      return self.authData && (self.authData.facebook || self.authData.google)
    },
    get passwordsMatch() {
      return (
        self.password.length &&
        self.passwordConfirmation.length &&
        self.password === self.passwordConfirmation
      )
    },
    get passwordChangeFieldsHaveError() {
      const KEYS_TO_VALIDATE = ['passwordConfirmation', 'password']
      const hasErrors = KEYS_TO_VALIDATE.reduce((hasError, key) => {
        if (hasError) return hasError
        return self.validateKey(key)
      }, false)
      return hasErrors
    },
    get registrationFieldsHaveError() {
      const authStore = getRoot(self).auth
      let KEYS_TO_VALIDATE = ['name', 'username', 'phoneNumber', 'email', 'password']
      if (authStore.canSignUpWithoutPassword) {
        KEYS_TO_VALIDATE = KEYS_TO_VALIDATE.filter((e) => e !== 'password')
      }
      const hasErrors = KEYS_TO_VALIDATE.reduce((hasError, key) => {
        if (hasError) return hasError
        return self.validateKey(key)
      }, false)
      return hasErrors
    },
  }))
  .actions((self) => ({
    showToast(message, type) {
      const all = getRoot(self)
      all.ui.toasts.addToast({
        message,
        type,
      })
    },
    validatePasswordChangeFields() {
      const KEYS_TO_VALIDATE = ['passwordConfirmation', 'password']
      self.validateKeyList(KEYS_TO_VALIDATE)
    },
    registrationFieldsHaveAsyncError: flow(function* () {
      const KEYS_TO_VALIDATE = ['phoneNumber']

      for (const key of KEYS_TO_VALIDATE) {
        const errorMessage = yield self.asyncValidateKey(key)
        if (errorMessage) return errorMessage
      }

      return false
    }),
    validateRegistrationFields: flow(function* () {
      const KEYS_TO_VALIDATE = ['name', 'username', 'phoneNumber', 'email', 'password']

      const ASYNC_KEYS_TO_VALIDATE = ['phoneNumber']

      self.validateKeyList(KEYS_TO_VALIDATE)
      yield self.asyncValidateKeyList(ASYNC_KEYS_TO_VALIDATE)
    }),
    setFirstName(value) {
      self.addTouched('firstName')
      self.name = `${value} ${self.lastName}`
    },
    setLastName(value) {
      self.addTouched('lastName')
      self.name = `${self.firstName} ${value}`
    },
    setEmail(value) {
      self.addTouched('email')
      self.email = value.toLowerCase().trim()
      if (!self.authData.facebook && !self.authData.google) {
        self.username = value.toLowerCase().trim()
      }
    },
    updateUserValues(dataObject) {
      const SAFE_KEYS = [
        'authData',
        'name',
        'username',
        'gender',
        'emailVerified',
        'email',
        'phoneNumber',
        'phoneNumberType',
        'phoneNumberVerified',
        'createdAt',
        'updatedAt',
        'paymentProcessor',
        'hasPaymentMethod',
        'licenseState',
        'licenseID',
        'address',
        'city',
        'countryState',
        'LastFour',
        'last4ssn',
        'dob',
        'zipcode',
        'credits',
        'utm',
        'gclid',
        'msclkid',
      ]
      const KEYS_TO_UPDATE = Object.keys(dataObject)
      for (let x = 0; x < KEYS_TO_UPDATE.length; x += 1) {
        const key = KEYS_TO_UPDATE[x]
        if (SAFE_KEYS.includes(key)) {
          switch (key) {
            case 'createdAt':
            case 'updatedAt':
              self[key] = new Date(dataObject[key])
              break
            default:
              self[key] = dataObject[key]
              break
          }
        }
      }
    },
    fetchCurrentUserData: flow(function* fetchCurrUserData() {
      if (!self.objectId || !self.objectId.length) return false
      // const lookupStore = getRoot(self).trafficLookup
      const results = yield AuthService.verifiedLogin(self.sessionToken, self.objectId)
      self.updateUserValues(results)
      // lookupStore.getUserFormData()
      return self
    }),
    updateUser: flow(function* updateUserGen(keys) {
      let success = false
      try {
        const data = self.buildDataUpdateObject(keys)
        if (data && self.objectId) {
          if (!data.paymentProcessor) {
            yield UserService.update(self.objectId, data, self.sessionToken)
            self.updateUserValues(yield UserService.update(self.objectId, data, self.sessionToken))
          }
          self.setLastUpdate()
          success = true
          self.showToast('Your account information has been updated.', 'SUCCESS')
        } else {
          success = true
        }
      } catch (err) {
        if (process.env.WINIT_ENV !== 'test') {
          console.error('error updating user', err)
        }
        success = false
      }
      return success
    }),
    createNewUser: flow(function* createNewUserGen({
      keys,
      locationState,
      addPaymentProcessor = false,
    }) {
      let success = false
      const rootStore = getRoot(self)
      try {
        const userType = yield self.getUserType(locationState)

        const data = { ...self.buildDataObject(keys), userType }

        yield UserService.signup(data)
        self.setLastUpdate()
        success = true
      } catch (err) {
        if (
          err === 'Account already exists for this username.' ||
          err === 'Account already exists for this email address.'
        ) {
          if (addPaymentProcessor) {
            rootStore.auth.setDisplayErrors([
              {
                target: 'email',
                message: 'This email is already registered.',
              },
            ])
          } else
            self.displayErrors.push({
              target: 'email',
              message: 'This email is already registered.',
            })
          if (rootStore.ui.isTrafficFlow || !addPaymentProcessor) {
            rootStore.ui.toasts.addToast({
              message: `${err} Please sign in with your email address`,
              type: 'ERROR',
            })
            setTimeout(() => {
              rootStore.ui.setSignupModalShowing(false)
              rootStore.ui.setLoginModalShowing(true)
            }, 5000)
          }
        }
        success = false
        if (process.env.WINIT_ENV !== 'test') {
          console.error('error creating new user', err)
        }
      }
      return success
    }),
    changePassword: flow(function* changePasswordGen() {
      if (!self.objectId || !self.objectId.length) {
        return false
      }
      if (self.passwordChangeFieldsHaveError) {
        self.validatePasswordChangeFields()
        return false
      }
      if (!self.passwordsMatch) {
        self.setDisplayErrors([
          {
            target: 'password',
            message: 'Provided passwords do not match.',
          },
        ])
        return false
      }
      const rootStore = getRoot(self)
      try {
        const updatedUser = yield self.updateUser(['password'])
        const reAuthedUser = yield AuthService.login(self.email, self.password)
        rootStore.auth.setUser(reAuthedUser)
        self.changeKey('accountCreatedWithRandomPassword', true)
        self.removeChangesForKey('password')
        self.removeChangesForKey('passwordConfirmation')
        self.setLastUpdate()
        return true
      } catch (err) {
        rootStore.ui.toasts.addToast({
          message:
            err ||
            `Sorry something unexpected happened. Please try again or contact us for further support.`,
          type: 'ERROR',
        })
        return false
      }
    }),
    updateAddress: (addressChange) => {
      self.address = addressChange.address
      self.city = addressChange.city
      self.countryState = addressChange.countryState
      self.zipcode = addressChange.zipcode
    },
    getUserType: flow(function* getUserType(locationState) {
      if (!locationState) return

      const { ui, trafficLookup, parkingFlow } = getRoot(self)

      if (
        ui.isPayNYCFlow ||
        parkingFlow.flow.isPayFlow ||
        parkingFlow.flow.isFightFlow ||
        parkingFlow.flow.isRobotPayFlow
      )
        return

      if (!trafficLookup.trafficStates.length) yield trafficLookup.loadTrafficStates()

      const isStateAffiliatedWithWinitLaw =
        trafficLookup.checkStateAffiliatedWithWinitLaw(locationState)

      if (isStateAffiliatedWithWinitLaw) return WINIT_LAW_USER_TYPE
    }),
    setUtmParameters(utmParameters) {
      self.utm = { ...utmParameters }
    },
    setGclid(gclid) {
      self.gclid = gclid || ''
    },
    setMsclkid(msclkid) {
      self.msclkid = msclkid || ''
    },
    setOrigin(origin) {
      self.origin = origin
    },
  }))

const PersistedChangesStore = buildPersistedChangesStore()

const CustomValidation = buildCustomValidationStore({
  validationObject: USER_VALIDATIONS,
  asyncValidationObject: USER_ASYNC_VALIDATIONS,
})

const User = types.compose(UserModelBase, PersistedChangesStore, CustomValidation)

export default User
